import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DatasetDownloadService } from '../../services/dataset-download.service';
import { formatDate } from '@angular/common';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';

export interface DatasetRow {
  key: string;
  nameKey: string;
  descriptionKey: string;
  maxRangeKey: string;
  form: FormGroup;
  // Dynamic datepicker constraints (updated reactively)
  endDateMin: Date;
  endDateMax: Date;
}

@Component({
  selector: 'app-dataset-download',
  templateUrl: './dataset-download.component.html',
  styleUrls: ['./dataset-download.component.scss'],
  standalone: false
})
export class DatasetDownloadComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private datasetDownloadService = inject(DatasetDownloadService);
  private router = inject(Router);
  protected translationService = inject(TranslationService);
  private featureflagsService = inject(FeatureflagsService);

  private readonly destroy$ = new Subject<void>();

  private readonly MAX_RANGE_DAYS = 365;
  private readonly DEFAULT_OFFSET_DAYS = 30;

  archiveForm: FormGroup;
  today = new Date();
  maxDate = new Date();
  datasets: DatasetRow[] = [];
  loadingDatasets = new Set<string>();
  displayedColumns = ['name', 'description', 'startDate', 'endDate', 'maxRange', 'action'];
  isDatasetDownloadEnabled = this.featureflagsService.getFeatureFlagValue('isDatasetDownloadEnabled');

  constructor() {}

  ngOnInit(): void {
    if (!this.isDatasetDownloadEnabled) {
      this.router.navigate(['/home/dashboard']);
      return;
    }
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    this.archiveForm = this.fb.group({
      startDate: [oneMonthAgo],
      endDate: [today]
    });

    const datasetKeys = [
      { key: 'audittrail', nameKey: 'audittrailDatasetText', descriptionKey: 'audittrailDatasetText' },
      { key: 'production', nameKey: 'productionDatasetText', descriptionKey: 'productionDatasetDescriptionText' },
      { key: 'materialRepair', nameKey: 'materialRepairDatasetText', descriptionKey: 'materialRepairDatasetText' },
      { key: 'materialCtc', nameKey: 'materialCtcDatasetText', descriptionKey: 'materialCtcDatasetText' },
      { key: 'packlist', nameKey: 'packlistDatasetText', descriptionKey: 'packlistDatasetText' },
      { key: 'materialTread', nameKey: 'materialTreadDatasetText', descriptionKey: 'materialTreadDatasetText' }
    ];

    this.datasets = datasetKeys.map(({ key, nameKey, descriptionKey }) => ({
      key,
      nameKey,
      descriptionKey,
      maxRangeKey: 'maxRangeCasingsText',
      form: this.fb.group({ startDate: [oneMonthAgo], endDate: [today] }),
      endDateMin: new Date(oneMonthAgo),
      endDateMax: this.clampToToday(this.addDays(oneMonthAgo, this.MAX_RANGE_DAYS), today)
    }));

    // Wire up reactive date constraints for each dataset row
    this.datasets.forEach((dataset) => this.bindDateConstraints(dataset, today));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // downloadArchive(): void {
  //   const startDate = this.archiveForm.get('startDate')?.value;
  //   const endDate = this.archiveForm.get('endDate')?.value;
  //   console.log('Download archive:', { startDate, endDate });
  //   // TODO: implement archive download API call
  // }

  isLoading(key: string): boolean {
    return this.loadingDatasets.has(key);
  }

  downloadDataset(dataset: DatasetRow): void {
    if (this.loadingDatasets.has(dataset.key)) {
      return; // prevent duplicate requests
    }

    const startDate = dataset.form.get('startDate')?.value;
    const endDate = dataset.form.get('endDate')?.value;
    const formattedStartDate = formatDate(startDate, 'yyyy-MM-dd', 'en-US');
    const formattedEndDate = formatDate(endDate, 'yyyy-MM-dd', 'en-US');

    this.loadingDatasets.add(dataset.key);
    console.log(`[DatasetDownload] Starting download for [${dataset.key}] from ${formattedStartDate} to ${formattedEndDate}`);

    this.datasetDownloadService.downloadDataset(dataset.key, formattedStartDate, formattedEndDate).subscribe({
      next: ({ datasetName, rows }) => {
        console.log(`[DatasetDownload] next() called — rows received: ${rows.length}`);
        this.loadingDatasets.delete(dataset.key);
        const fileName = `${datasetName || dataset.key}.csv`;
        this.exportToCsv(fileName, rows);
      },
      error: (err) => {
        this.loadingDatasets.delete(dataset.key);
        console.error(`[DatasetDownload] Error for [${dataset.key}]:`, err);
      },
      complete: () => {
        if (this.loadingDatasets.has(dataset.key)) {
          console.warn(`[DatasetDownload] Stream completed without allDataProcessed=true for [${dataset.key}]. Check backend response.`);
          this.loadingDatasets.delete(dataset.key);
        }
      }
    });
  }

  private exportToCsv(fileName: string, rows: string[]): void {
    const url = URL.createObjectURL(new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' }));
    Object.assign(document.createElement('a'), { href: url, download: fileName }).click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  private bindDateConstraints(dataset: DatasetRow, today: Date): void {
    const form = dataset.form;

    // When start date changes: auto-set end date to startDate+30 and update end date bounds.
    // emitEvent: true keeps MatDatepickerInput's internal _value in sync.
    form
      .get('startDate')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((startDate: Date) => {
        if (!startDate) return;
        dataset.endDateMin = new Date(startDate);
        dataset.endDateMax = this.clampToToday(this.addDays(startDate, this.MAX_RANGE_DAYS), today);
        const autoEnd = this.clampToToday(this.addDays(startDate, this.DEFAULT_OFFSET_DAYS), today);
        form.get('endDate')!.setValue(autoEnd);
      });
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  private clampToToday(date: Date, today: Date): Date {
    return date > today ? new Date(today) : date;
  }
}
